import { Link } from "react-router-dom";
import { ArrowUpRight, Instagram, Twitter, Linkedin, Youtube } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LanguageProvider";

export default function Footer() {
  const { t } = useTranslation();
  const { prefix } = useLang();

  return (
    <footer className="relative bg-forest text-canvas noise overflow-hidden" data-testid="site-footer">
      <div className="container-x pt-24 pb-10">
        {/* Top CTA row */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div className="label-eyebrow !text-lime">{t("footer.ctaEyebrow")}</div>
            <h3 className="mt-6 font-display font-medium tracking-tightest text-canvas text-5xl sm:text-6xl lg:text-7xl leading-[0.92]">
              {t("footer.ctaTitle")}<br />
              <span className="text-lime">{t("footer.ctaTitleItalic")}</span>
            </h3>
          </div>
          <div className="lg:col-span-4 flex lg:justify-end">
            <Link to={prefix("/waitlist")} className="btn-accent" data-testid="footer-waitlist-cta">
              {t("footer.ctaBtn")} <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="h-px w-full bg-canvas/15 mt-16" />

        {/* Link columns */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mt-14">
          <FooterCol
            title={t("footer.colProduct")}
            links={[
              { to: "/how-it-works", label: t("nav.howItWorks") },
              { to: "/fees", label: t("nav.fees") },
              { to: "/countries", label: t("nav.corridors") },
              { to: "/security", label: t("nav.security") },
            ]}
            prefix={prefix}
          />
          <FooterCol
            title={t("footer.colCompany")}
            links={[
              { to: "/about", label: t("footer.linkAbout") },
              { to: "/faq", label: t("nav.faq") },
              { to: "/contact", label: t("nav.contact") },
              { to: "/waitlist", label: t("nav.joinWaitlist") },
              { to: "/founders", label: t("footer.linkFounders") },
              { to: "/press", label: t("footer.linkPress") },
            ]}
            prefix={prefix}
          />
          <FooterCol
            title={t("footer.colLegal")}
            links={[
              { to: "/legal/terms", label: t("footer.terms") },
              { to: "/legal/privacy", label: t("footer.privacy") },
              { to: "/legal/aml-kyc", label: t("footer.amlKyc") },
              { to: "/legal/cookies", label: t("footer.cookies") },
            ]}
            prefix={prefix}
          />
          <div>
            <div className="label-eyebrow !text-canvas/60">{t("footer.colReach")}</div>
            <div className="mt-4 space-y-2 text-canvas/80">
              <a href="mailto:support@instantxfer.com" className="link-reveal block" data-testid="footer-email">
                support@instantxfer.com
              </a>
              <a
                href="https://wa.me/1775387466"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-lime/15 hover:bg-lime/25 text-lime hover:text-canvas border border-lime/25 hover:border-lime/60 transition-colors text-sm font-medium"
                data-testid="footer-whatsapp"
                aria-label={t("footer.chatWhatsApp")}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden>
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.198-.347.223-.644.074-.297-.148-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.148-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.695.625.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347zM12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.304-1.654a11.87 11.87 0 005.741 1.462h.005c6.554 0 11.89-5.335 11.893-11.892C23.944 5.335 18.609 0 12.05 0z"/>
                </svg>
                {t("footer.chatWhatsApp")}
              </a>
              <div className="text-canvas/60 text-sm">
                Addis Ababa · Amsterdam · Birmingham · Brussels · Cape Town · Douala · Lagos · London · Nairobi
              </div>
            </div>
            <div className="flex items-center gap-3 mt-5">
              {[Twitter, Instagram, Linkedin, Youtube].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  aria-label="social"
                  className="w-10 h-10 rounded-full border border-canvas/20 flex items-center justify-center hover:bg-canvas/10 transition-colors"
                  data-testid={`footer-social-${i}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Massive wordmark */}
        <div className="relative mt-20 pointer-events-none select-none">
          <div className="text-[min(15vw,200px)] leading-[0.85] font-display font-medium tracking-tightest text-canvas/95 whitespace-nowrap">
            INSTANTXFER
          </div>
          <div className="mt-2 flex items-baseline justify-between gap-4">
            <div className="text-lime text-[min(6.5vw,90px)] leading-none font-display italic font-serif whitespace-nowrap">
              CashApp
            </div>
            <div className="text-canvas/70 text-xs tracking-[0.22em] uppercase mb-2 shrink-0">
              www.instantxfer.com
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-canvas/60">
          <div>© {new Date().getFullYear()} Instantxfer CashApp Financial Technologies. {t("footer.rights")}</div>
          <div className="flex items-center gap-4">
            <span className="inline-flex items-center gap-2">
              <span className="live-dot" /> {t("footer.systemsNominal")}
            </span>
            <span>v1.0 · Marketing</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ title, links, prefix }) {
  return (
    <div>
      <div className="label-eyebrow !text-canvas/60">{title}</div>
      <ul className="mt-4 space-y-2">
        {links.map((l) => (
          <li key={l.to}>
            <Link to={prefix(l.to)} className="link-reveal text-canvas/85" data-testid={`footer-link-${l.to.replace(/\//g, "-")}`}>
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
